import { useQueries } from '@tanstack/react-query';
import type { PromoCodes } from 'hooks/cart/useDummyCart';
import { createCacheKey, createDummyCart } from 'hooks/cart/useDummyCart';
import { useLocalStorage, UserLocale } from 'hooks/userPreferences';
import { useMemo } from 'react';
import type { ShopifyProduct } from 'types/product/ShopifyProduct';
import { getBlockSellingPlans } from 'utils/getBlockSellingPlans';
import { updateProductPrices } from 'utils/product/updateProductPrices';

export const PRODUCTS_UPDATED_PRICES_KEY = 'productsUpdatedPrices';

interface UseProductsUpdatedPricesOptions {
  blockId: string;
  shopifyProducts?: ShopifyProduct[] | null;
  promoCodes?: PromoCodes;
}

/* INFO: Each product gets its OWN dummy cart so that an order-level discount
 * (e.g. a "once per order" code) is applied in full to that product, rather
 * than being split across every product in a single combined cart. This
 * matters for the SupplementPicker, which renders two distinct products in one
 * block. Single-product callers behave exactly as before. */
export const useProductsUpdatedPrices = ({
  blockId,
  shopifyProducts,
  promoCodes,
}: UseProductsUpdatedPricesOptions) => {
  const {
    value: { countryCode },
    isLoading: isLocaleLoading,
  } = useLocalStorage(UserLocale);

  const hasPromoCodes = !!promoCodes && promoCodes.length > 0;
  const products = useMemo(() => shopifyProducts ?? [], [shopifyProducts]);

  const queries = useQueries({
    queries: products.map((product) => {
      const lines = getBlockSellingPlans(product) || [];
      return {
        queryKey: [
          PRODUCTS_UPDATED_PRICES_KEY,
          blockId,
          product.id,
          countryCode,
          createCacheKey(lines, promoCodes),
        ],
        queryFn: () => createDummyCart({ lines, promoCodes, countryCode }),
        enabled: hasPromoCodes && !isLocaleLoading && lines.length > 0,
      };
    }),
  });

  const isLoading = queries.some((query) => query.isLoading);
  const isFetching = queries.some((query) => query.isFetching);
  const isError = queries.some((query) => query.isError);
  const error = queries.find((query) => query.error)?.error ?? null;

  /* Stable dependency for the memo below: changes whenever any product's cart
   * resolves to a different cart, without allocating a new array each render. */
  const cartsKey = queries.map((query) => query.data?.id ?? 'none').join('|');

  const updatedProducts = useMemo(() => {
    if (!shopifyProducts) return null;

    return shopifyProducts.map((product, index) => {
      const cart = queries[index]?.data;
      return cart
        ? updateProductPrices({ cart, shopifyProduct: product })
        : product;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shopifyProducts, cartsKey]);

  return {
    data: updatedProducts,
    isLoading,
    isError,
    isFetching,
    error,
  };
};
